import * as yup from "yup";
import AppText from "../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../styles/GlobalStyles";
import React, {useMemo} from "react";
import SessionAutoMessage from "./FlowComponents/SessionAutoMessage";
import {t} from "../../../../services/i18n";
import {autoMessageTypesObj, EMAIL, multiPurchaseTypeToMtType, PUSH, SMS, WHATSAPP} from "../../../../services/ConstHelper";
import {dbDateFormat, dbTimeFormat, getSessionAutoMessageTags, isMobile} from "../../../../services/Helpers";
import {
    autoMessageTimeUnits,
    autoMessageTypes,
    getIsMessageTypeActive
} from "../../Schedule/Popups/ScheduleSettings/SettingsConfig";
import {fetchApi} from "../../../../services/HTTP";
import {getAutoActions} from "../../../../redux/boxSlice";
import {toggleScheduleLoader} from "../../../../redux/ScheduleSlice";
import moment from "moment";
import {VStack} from "native-base";
import MultiPurchase from "./FlowComponents/MultiPurchase";
import {hideLoader, showLoader} from "../../../../redux/rootManagerSlice";
import {store} from "../../../../redux/store";
import messageType from "../../Forms/Messages/MessageType";
import {useSelector} from "react-redux";
import GeneralNote from "./FlowComponents/GeneralNote";
import {sendMessageToAngular} from "../../../../services/IframeIntegrationServices";

export const modalFlowOptions = {
    SESSION_AUTO_MESSAGE: 'SESSION_AUTO_MESSAGE',
    MULTI_PURCHASE: 'MULTI_PURCHASE',
    GENERAL_NOTE: 'GENERAL_NOTE'
}

export const getValidationSchema = ({flowName, initProps}) => {
    switch (flowName) {
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            const autoActions = store.getState().box.box.auto_actions
            const existingFirstReminder = autoActions?.filter(item => item.auto_actions.action === autoMessageTypes.REMINDER_SESSION && item.active === 1 && item.session_type === initProps?.sessionType)
            const existingSecondReminder = autoActions?.filter(item => item.auto_actions.action === autoMessageTypes.SECOND_REMINDER_SESSION && item.active === 1 && item.session_type === initProps?.sessionType)

            return yup.object().shape({
                actionName: yup.string().required(t('one-type-minimum')).oneOf(Object.values(autoMessageTypes)),
                active: yup.boolean().required(),
                type: yup.array(yup.object().shape({
                    id: yup.string().required(t('one-type-minimum')).oneOf([PUSH, SMS, EMAIL, WHATSAPP])
                })).min(1, t('one-type-minimum')).required(t('one-type-minimum')),
                subject: yup.string().when('type', {
                    is: value => value.some(item => item.id === EMAIL),
                    then: yup.string().required(t('thisFieldIsMandatory')),
                    otherwise: yup.string()
                }),
                message_content: yup.string().required(t('thisFieldIsMandatory')).when('type', {
                    is: value => value.some(item => item.id === PUSH),
                    then: yup.string().max(175, ''),
                    otherwise: yup.string().max(1000, '')
                }),
                digital_form_id: yup.number().nullable(),
                period_amount: yup.number()
                    .when('actionName', {
                        is: autoMessageTypes.REMINDER_SESSION,
                        then: yup.number().positive().integer().min(1).required(t('thisFieldIsMandatory'))
                            .test('first-after-second-when-same-unit',
                                t('first-reminder-must-be-after-second', {period_amount: existingSecondReminder[0]?.period_amount, period_time_unit: t(`${existingSecondReminder[0]?.period_time_unit?.toLowerCase()}s`)}),
                                function (value) {
                                if (!value || existingSecondReminder.length === 0) return true;
                                const sameUnit = this.parent?.period_time_unit === existingSecondReminder[0].period_time_unit;
                                if (!sameUnit) return true;
                                return value > existingSecondReminder[0].period_amount;
                            }),
                        otherwise: yup.number().nullable()
                    })
                    .when('actionName', {
                        is: autoMessageTypes.SECOND_REMINDER_SESSION,
                        then: yup.number().positive().integer().min(1).required(t('thisFieldIsMandatory'))
                            .test('first-after-second-when-same-unit',
                                t('first-reminder-must-be-before-second', {period_amount: existingFirstReminder[0]?.period_amount, period_time_unit: t(`${existingFirstReminder[0]?.period_time_unit?.toLowerCase()}s`)}),
                                function (value) {
                                    if (!value || existingFirstReminder.length === 0) return true;
                                    const sameUnit = this.parent?.period_time_unit === existingFirstReminder[0].period_time_unit;
                                    if (!sameUnit) return true;
                                    return value < existingFirstReminder[0].period_amount;
                                }),
                        otherwise: yup.number().nullable()
                    }),
                period_time_unit: yup.string().when('actionName', {
                    is: autoMessageTypes.REMINDER_SESSION,
                    then: yup.string().required(t('thisFieldIsMandatory')).oneOf(Object.values(autoMessageTimeUnits)),
                    otherwise: yup.string().nullable()
                })
            })
        case modalFlowOptions.MULTI_PURCHASE:
            return yup.object().shape({
                membership_type: yup.object().shape({
                    id: yup.number().positive()
                }).required(t('thisFieldIsMandatory')).typeError(t('thisFieldIsMandatory')),
                start_date: yup.string().test('date-format', 'invalid date', (value) => value && (moment(value, dbDateFormat).format(dbDateFormat) === value)),
                end_date: yup.string().nullable().test('date-format', 'invalid date', function(value){
                    return !value || (value && (moment(value, dbDateFormat).format(dbDateFormat) === value))
                }).when(['start_date'], (start_date, schema) => {
                    const startDate = moment(start_date, dbDateFormat)
                    return schema.test({
                        test: endDate => {
                            return !endDate || moment(endDate, dbDateFormat).isAfter(startDate)
                        },
                        message: t('shiftDateError')
                    })
                }),
                comment: yup.string().nullable(),
                users: yup.array().of(yup.number().required(t('thisFieldIsMandatory'))).ensure()
            })
        case modalFlowOptions.GENERAL_NOTE:
            return yup.object().shape({
                id: yup.number().nullable(),
                text: yup.string().required(t('thisFieldIsMandatory')).test(
                    'has-content',
                    t('thisFieldIsMandatory'),
                    (value) => {
                        if (!value) return false;
                        const textOnly = value.replace(/<[^>]*>/g, '').trim();
                        return textOnly !== '';
                    }
                ),
                users_id: yup.number().when('group', {
                    is: value => value === null, //when there is a group, the users_id is not in use, but instead group ids array is used
                    then: yup.number().required(t('thisFieldIsMandatory')),
                    otherwise: yup.number().nullable()
                }),
                pop: yup.number().min(0).max(3),
                group: yup.array().of(yup.number().positive()).min(1, t('thisFieldIsMandatory')).nullable()
            })
        default:
            return yup.object().shape({})
    }
}

export const getInitialValues = ({flowName, target, initProps}) => {
    switch (flowName) {
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            return getSessionAutoMessageInitialValues({target, initProps})
        case modalFlowOptions.MULTI_PURCHASE:
            return getMultiPurchaseInitialValues({initProps})
        case modalFlowOptions.GENERAL_NOTE:
            return getGeneralNoteInitialValues({target, initProps})
        default:
            return {}
    }
}

const getSessionAutoMessageInitialValues = ({target, initProps}) => {
    const messageTypeAutoActions = target ?? []
    const types = messageTypeAutoActions.filter(item => item.active).map(item => autoMessageTypesObj({})[item.auto_action_type.toLowerCase()])
    const messageTemplate = messageTypeAutoActions.length ? messageTypeAutoActions[0].message_template : {}
    let message_content = messageTemplate.text ?? null
    if(message_content) {
        const tags = getSessionAutoMessageTags(initProps?.messageType, true, initProps?.sessionType)
        tags.forEach(tag => {
            message_content = message_content.replaceAll(`{{#${tag.id}#}}`, `[${tag.label}]`)
        });
    }
    const isReminder = initProps?.messageType === autoMessageTypes.REMINDER_SESSION || initProps?.messageType === autoMessageTypes.SECOND_REMINDER_SESSION
    const isCheckInReminder = initProps?.messageType === autoMessageTypes.MARK_CHECK_IN
    const isMissedTrialReminder = initProps?.messageType === autoMessageTypes.MISSED_TRIAL_SESSION
    const autoActionSetFirst = (isReminder || isCheckInReminder || isMissedTrialReminder) && messageTypeAutoActions.length > 0 ? messageTypeAutoActions[0] : null
    return {
        actionName: initProps?.messageType,
        type: types ?? [],
        subject: messageTemplate.subject ?? '',
        message_content: message_content ?? t(`default-message-content-${initProps?.messageType}`),
        digital_form_id: messageTemplate.digital_form_id ?? null,
        active: types.length > 0,
        period_amount: autoActionSetFirst?.period_amount ?? getDefaultPeriodAmount(initProps?.messageType),
        period_time_unit: autoActionSetFirst?.period_time_unit ?? getDefaultPeriodTimeUnit(initProps?.messageType),
        count: isCheckInReminder ? (autoActionSetFirst?.count ?? 1) : null,
        session_type: initProps?.sessionType ?? null
    }
}

const getDefaultPeriodAmount = (messageType) => {
    switch (messageType) {
        case autoMessageTypes.REMINDER_SESSION:
            return 3
        case autoMessageTypes.SECOND_REMINDER_SESSION:
        case autoMessageTypes.MISSED_TRIAL_SESSION:
            return 1
        case autoMessageTypes.MARK_CHECK_IN:
            return 15
        default:
            return null
    }
}

const getDefaultPeriodTimeUnit = (messageType) => {
    switch (messageType) {
        case autoMessageTypes.REMINDER_SESSION:
        case autoMessageTypes.SECOND_REMINDER_SESSION:
        case autoMessageTypes.MISSED_TRIAL_SESSION:
            return autoMessageTimeUnits.HOUR
        default:
            return null
    }
}


const getMultiPurchaseInitialValues = ({initProps}) => {
    return {
        membership_type: null,
        start_date: moment().format(dbDateFormat),
        end_date: null,
        endDateRes: null,
        comment: null,
        users: initProps?.selectedUsers ? Object.keys(initProps.selectedUsers).map(user_id => parseInt(user_id)) : []
    }
}

const getGeneralNoteInitialValues = ({target, initProps}) => {
    let group = []
    if(!target && initProps.group_data) {
        group = initProps.group_data.map(m => m.user_box.user_fk)
    }
    return {
        id: target?.id ?? null,
        text: target?.text ?? null,
        users_id: group.length > 1 ? null : initProps?.users_id,
        pop: target?.pop ?? 0,
        group: group.length > 1 ? group : null
    }
}

export const getHeader = ({flowName, initProps, target}) => {
    switch (flowName) {
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            return <AppText fontSize={GlobalStyleAttributes.fonts.mainHeader} boldFont>{t(`edit-template-${initProps?.messageType}${initProps?.messageType === 'REMINDER_SESSION' && initProps?.sessionType === null ? '-group-session-courses' : ''}${isMobile ? '-short' : ''}`)}</AppText>
        case modalFlowOptions.MULTI_PURCHASE:
            const type = multiPurchaseTypeToMtType[initProps.type]
            return (
                <VStack space={'8px'}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.mainHeader} boldFont>{t(`multiPurchaseHeader-${type}`)}</AppText>
                    <AppText>{t(`multiPurchaseSubHeader-${type}`, { num: initProps?.selectedUsers ? Object.keys(initProps.selectedUsers).length : 0})}</AppText>
                </VStack>
            )
        case modalFlowOptions.GENERAL_NOTE:
            return <AppText fontSize={GlobalStyleAttributes.fonts.mainHeader} boldFont>{t(`${target ? 'edit' : 'create'}-general-note-header`)}</AppText>
        default:
            return null
    }
}

export const renderBody = ({flowName, initProps, ...rest}) => {
    switch (flowName) {
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            const isReminder = initProps?.messageType === autoMessageTypes.REMINDER_SESSION || initProps?.messageType === autoMessageTypes.SECOND_REMINDER_SESSION
            const isMissedTrialSession = initProps?.messageType === autoMessageTypes.MISSED_TRIAL_SESSION
            const showCheckInSection = initProps?.messageType === autoMessageTypes.MARK_CHECK_IN
            const showDigitalFormSection = initProps?.messageType !== autoMessageTypes.MARK_CHECK_IN
            return <SessionAutoMessage
                isReminder={isReminder}
                allowTimeSelection={isReminder || isMissedTrialSession}
                showCheckInSection={showCheckInSection}
                showDigitalFormSection={showDigitalFormSection}
                messageType={initProps?.messageType}
                periodAmountTextBefore={initProps?.periodAmountTextBefore}
                periodAmountTextAfter={initProps?.periodAmountTextAfter}
                {...rest}/>
        case modalFlowOptions.MULTI_PURCHASE:
            return <MultiPurchase type={multiPurchaseTypeToMtType[initProps.type] ?? ''} {...rest}/>
        case modalFlowOptions.GENERAL_NOTE:
            let additional = {
                groupData: initProps.group_data ?? null,
            }
            return <GeneralNote {...additional} {...rest}/>
        default:
            return null
    }
}

export const onSaveCallback = ({flowName}) => {
    switch (flowName) {
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            return (props) => updateSessionAutoMessage(props)
        case modalFlowOptions.MULTI_PURCHASE:
            return (props) => createMultiPurchases(props)
        case modalFlowOptions.GENERAL_NOTE:
            return (props) => saveGeneralNote(props)
        default:
            return null
    }
}

export const getFlowConfirm = ({flowName, ...rest}) => {
    const {values, onConfirm} = rest
    switch (flowName) {
        case modalFlowOptions.MULTI_PURCHASE:
            return multiPurchaseConfirm({...rest})
        case modalFlowOptions.SESSION_AUTO_MESSAGE:
            if(getIsMessageTypeActive(autoMessageTypes.SECOND_REMINDER_SESSION) && !values.active && values.actionName === autoMessageTypes.REMINDER_SESSION) {
                return {
                    header: t('areYouSure'),
                    subheader: t('confirm-turn-off-first-and-sec-reminder'),
                    confirmText: t('confirm'),
                    cancelText: t('cancel'),
                    onConfirm: () => onConfirm()
                }
            }
            return null
        default:
            return null
    }
}

export const getFlowWidth = ({flowName}) => {
    switch (flowName) {
        case modalFlowOptions.MULTI_PURCHASE:
            return '600px'
        default:
            return null
    }
}

const updateSessionAutoMessage = async ({values, dispatch, toast}) => {
    try {
        dispatch(toggleScheduleLoader(true));
        const tags = getSessionAutoMessageTags(values.actionName, true, values.session_type)
        let message_content = values.message_content
        tags.forEach(tag => {
            message_content = message_content.replaceAll(`[${tag.label}]`, `{{#${tag.id}#}}`)
        });
        const params = {...values, message_content, type: values.type.map(item => item.id)}
        await fetchApi('autoActionsSet/updateSessionAutoMessage', 'post', params, false, true)
        dispatch(getAutoActions())
        dispatch(toggleScheduleLoader(false));
        toast.show(t('savedSuccessfully'),{type: "success", duration: 2000})
    } catch(error) {
        dispatch(toggleScheduleLoader(false));
        toast.show(t('error-updating-session-auto-message'),{type: "danger", duration: 4000})
        console.log('error in updateSessionAutoMessage', error)
    }
}

const createMultiPurchases = async ({values, dispatch, toast, initProps}) => {
    try {
        dispatch(showLoader())
        await fetchApi('multiPurchase', 'post', values, false, true)
        dispatch(hideLoader())
        toast.show(t('success-multi-purchase', {numUsers: values.users.length, name: values.membership_type.name}),{type: "success", duration: 4000})
        if(initProps?.onSuccess) initProps.onSuccess()
    } catch(error) {
        toast.show(t('error-multi-purchase', {numUsers: values.users.length, name: values.membership_type.name}),{type: "danger", duration: 4000})
        console.log('error in createMultiPurchases', error)
    }
}
const saveGeneralNote = async ({values, dispatch, toast, initProps}) => {
    try {
        dispatch(showLoader())
        const res = await fetchApi('notes/createOrUpdateUserNote', 'POST', {...values, general: true}, false, true);
        sendMessageToAngular('refresh_general_notes', {}, 'main_angular_frame_id')
        dispatch(hideLoader())
        toast.show(t('savedSuccessfully'),{type: "success", duration: 2000});
    } catch(error) {
        console.log('error in saveGeneralNote', error)
        toast.show(t('somethingWentWrongPleaseTryAgain'),{type: "danger", duration: 4000});
    }
}

const multiPurchaseConfirm = ({values, onConfirm}) => {
    const box = store.getState().box.box
    const date_format = box.locations_box[0].date_format
    return {
        header: t('confirm-header-multiple-purchase'),
        subheader: t(`confirm-subheader-multiple-purchase${(values.endDateRes !== 'unlimited' || values.end_date) ? '' : '-unlimited'}`, {
            type: t(`membership-type-${values.membership_type?.type}`),
            numUsers: values.users?.length ?? 0,
            name: values.membership_type?.name,
            start_date: moment(values.start_date).format(date_format),
            end_date: (values.endDateRes !== 'unlimited' || values.end_date) ? moment(values.end_date ?? values.endDateRes).format(date_format) : null
        }),
        confirmText: t('confirm'),
        cancelText: t('cancel'),
        onConfirm: () => onConfirm()
    }
}